import { useState, useEffect } from 'react'
import './CookieConsent.css'

// Simple SVG Icons
const CookieIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <circle cx="15.5" cy="8.5" r="1.5" />
    <circle cx="8.5" cy="15.5" r="1.5" />
    <circle cx="15.5" cy="15.5" r="1.5" />
  </svg>
)

const XIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const FileTextIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
)

function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false)
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false)
  const [showCookieSettings, setShowCookieSettings] = useState(false)

  useEffect(() => {
    // Проверяем, принял ли пользователь решение относительно cookies
    const cookieConsentStr = localStorage.getItem('cookieConsent')
    if (!cookieConsentStr) {
      // Показываем баннер через небольшую задержку для лучшего UX
      const timer = setTimeout(() => {
        setShowBanner(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
    
    // Проверяем, не истекло ли согласие (через 1 год)
    try {
      const consentData = JSON.parse(cookieConsentStr)
      const consentDate = new Date(consentData.date)
      const oneYearAgo = new Date()
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
      
      if (consentDate < oneYearAgo) {
        // Согласие истекло, показываем баннер снова
        localStorage.removeItem('cookieConsent')
        const timer = setTimeout(() => {
          setShowBanner(true)
        }, 1000)
        return () => clearTimeout(timer)
      }
    } catch (e) {
      // Если данные в старом формате, показываем баннер
      const timer = setTimeout(() => {
        setShowBanner(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleRevokeConsent = () => {
    localStorage.removeItem('cookieConsent')
    document.cookie = 'cookieConsent=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
    setShowBanner(true)
  }

  const handleAccept = () => {
    const consentData = {
      consent: 'accepted',
      date: new Date().toISOString(),
      version: '1.0'
    }
    localStorage.setItem('cookieConsent', JSON.stringify(consentData))
    setShowBanner(false)
    // Устанавливаем cookie для сервера (если нужно)
    document.cookie = `cookieConsent=accepted; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`
  }

  const handleDecline = () => {
    const consentData = {
      consent: 'declined',
      date: new Date().toISOString(),
      version: '1.0'
    }
    localStorage.setItem('cookieConsent', JSON.stringify(consentData))
    setShowBanner(false)
    // Удаляем все cookies кроме необходимых
    document.cookie = `cookieConsent=declined; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`
  }

  if (!showBanner) return null

  return (
    <>
      <div className="cookie-consent">
        <div className="cookie-consent-content">
          <div className="cookie-consent-icon">
            <CookieIcon size={24} />
          </div>
          <div className="cookie-consent-text">
            <h3>Používání cookies</h3>
            <p>
              Tento web používá cookies pro zajištění fungování systému, ukládání vašich nastavení a 
              zlepšení uživatelského zážitku. Pokračováním v používání webu souhlasíte s používáním 
              cookies v souladu s naší{' '}
              <button
                onClick={(e) => {
                  e.preventDefault()
                  setShowPrivacyPolicy(true)
                }}
                className="cookie-link"
              >
                Zásadami ochrany osobních údajů
              </button>
              .
            </p>
          </div>
          <div className="cookie-consent-buttons">
            <button onClick={handleAccept} className="cookie-button accept">
              Přijmout
            </button>
            <button onClick={() => setShowCookieSettings(true)} className="cookie-button settings">
              Nastavení
            </button>
            <button onClick={handleDecline} className="cookie-button decline">
              Odmítnout
            </button>
            <button
              onClick={handleDecline}
              className="cookie-button close"
              aria-label="Zavřít"
            >
              <XIcon size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Модальное окно политики конфиденциальности */}
      {showPrivacyPolicy && (
        <div 
          className="cookie-modal-overlay"
          onClick={() => setShowPrivacyPolicy(false)}
        >
          <div 
            className="cookie-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cookie-modal-header">
              <div className="cookie-modal-header-content">
                <div className="cookie-modal-icon">
                  <FileTextIcon size={20} />
                </div>
                <h2>Zásady ochrany osobních údajů</h2>
              </div>
              <button
                onClick={() => setShowPrivacyPolicy(false)}
                className="cookie-modal-close"
                aria-label="Zavřít"
              >
                <XIcon size={24} />
              </button>
            </div>

            <div className="cookie-modal-body">
              <div className="cookie-policy-section">
                <h3>1. Obecná ustanovení</h3>
                <p>
                  Tyto Zásady ochrany osobních údajů popisují, jak shromažďujeme, používáme a chráníme vaše osobní údaje 
                  při používání našeho webového systému pro správu školy.
                </p>
              </div>

              <div className="cookie-policy-section">
                <h3>2. Jaké informace shromažďujeme</h3>
                <p>Shromažďujeme následující informace:</p>
                <ul>
                  <li>Jméno a příjmení</li>
                  <li>E-mailová adresa</li>
                  <li>Telefonní číslo</li>
                  <li>Informace o zaměstnání a pozici</li>
                  <li>Data pro autentizaci (pro zaměstnance)</li>
                  <li>Informace o hlášeních závad a žádostech</li>
                </ul>
              </div>

              <div className="cookie-policy-section">
                <h3>3. Používání cookies</h3>
                <p>
                  Používáme cookies pro zajištění fungování webu, ukládání uživatelských relací a zlepšení 
                  uživatelského zážitku. Cookies jsou malé textové soubory, které se ukládají na vašem zařízení.
                </p>
                <p className="cookie-policy-subtitle">
                  <strong>Typy cookies, které používáme:</strong>
                </p>
                <ul>
                  <li><strong>Nezbytné cookies (vždy aktivní):</strong> pro autentizaci, bezpečnost a základní funkčnost webu. Tyto cookies nelze deaktivovat.</li>
                  <li><strong>Funkční cookies:</strong> pro ukládání vašich nastavení a zlepšení uživatelského zážitku. Tyto cookies můžete deaktivovat.</li>
                </ul>
                <p className="cookie-policy-subtitle">
                  <strong>Doba uchování:</strong> Cookies se ukládají až na 1 rok nebo do doby, kdy je smažete v nastavení prohlížeče.
                </p>
              </div>

              <div className="cookie-policy-section">
                <h3>4. Jak používáme vaše informace</h3>
                <p>Vaše osobní údaje používáme pro:</p>
                <ul>
                  <li>Zpracování a řešení vašich hlášení závad a žádostí</li>
                  <li>Komunikaci s vámi ohledně vašich požadavků</li>
                  <li>Zlepšení kvality služeb</li>
                  <li>Zajištění bezpečnosti a prevence podvodů</li>
                  <li>Správu zaměstnanců a uživatelů systému</li>
                </ul>
              </div>

              <div className="cookie-policy-section">
                <h3>5. Ochrana údajů</h3>
                <p>
                  Používáme technická a organizační opatření k ochraně vašich osobních údajů před 
                  neoprávněným přístupem, ztrátou nebo zničením. Vaše data jsou uložena v šifrované podobě 
                  na bezpečných serverech.
                </p>
              </div>

              <div className="cookie-policy-section">
                <h3>6. Vaše práva podle GDPR</h3>
                <p>Podle Obecného nařízení o ochraně údajů (GDPR) máte právo:</p>
                <ul>
                  <li><strong>Právo na přístup:</strong> získat informace o zpracování vašich osobních údajů</li>
                  <li><strong>Právo na opravu:</strong> opravit nepřesné nebo neúplné údaje</li>
                  <li><strong>Právo na výmaz:</strong> požadovat smazání vašich údajů ("právo být zapomenut")</li>
                  <li><strong>Právo na omezení zpracování:</strong> omezit zpracování vašich údajů</li>
                  <li><strong>Právo na přenositelnost údajů:</strong> získat své údaje ve strukturovaném formátu</li>
                  <li><strong>Právo na odvolání souhlasu:</strong> odvolat souhlas se zpracováním údajů kdykoli</li>
                  <li><strong>Právo na námitku:</strong> namítat proti zpracování vašich údajů</li>
                </ul>
                <p className="cookie-policy-subtitle">
                  Pro uplatnění kterékoli z těchto práv se prosím obraťte na nás prostřednictvím systému nebo na e-mail uvedený v systému.
                </p>
              </div>

              <div className="cookie-policy-section">
                <h3>7. Kontaktní informace a správce údajů</h3>
                <p>
                  Správcem osobních údajů je vlastník systému správy školy.
                </p>
                <p className="cookie-policy-subtitle">
                  Pokud máte dotazy ohledně těchto Zásad ochrany osobních údajů, zpracování vašich osobních údajů nebo chcete uplatnit svá práva podle GDPR, 
                  prosím kontaktujte nás prostřednictvím systému nebo na e-mail uvedený v systému.
                </p>
                <p className="cookie-policy-subtitle">
                  Máte také právo podat stížnost dozorčímu orgánu pro ochranu údajů, pokud se domníváte, že zpracování vašich osobních údajů porušuje GDPR.
                </p>
              </div>

              <div className="cookie-policy-section">
                <h3>8. Změny v Zásadách</h3>
                <p>
                  Tyto Zásady ochrany osobních údajů můžeme pravidelně aktualizovat. O významných změnách vás budeme informovat 
                  prostřednictvím našeho webového systému.
                </p>
              </div>

              <div className="cookie-policy-footer">
                <p>Poslední aktualizace: {new Date().toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
            </div>

            <div className="cookie-modal-footer">
              <button
                onClick={() => setShowPrivacyPolicy(false)}
                className="cookie-button accept"
              >
                Rozumím
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно настроек cookies */}
      {showCookieSettings && (
        <div 
          className="cookie-modal-overlay"
          onClick={() => setShowCookieSettings(false)}
        >
          <div 
            className="cookie-modal cookie-settings-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cookie-modal-header">
              <div className="cookie-modal-header-content">
                <div className="cookie-modal-icon">
                  <CookieIcon size={20} />
                </div>
                <h2>Nastavení cookies</h2>
              </div>
              <button
                onClick={() => setShowCookieSettings(false)}
                className="cookie-modal-close"
                aria-label="Zavřít"
              >
                <XIcon size={24} />
              </button>
            </div>

            <div className="cookie-modal-body">
              <div className="cookie-settings-section">
                <h3>Nezbytné cookies (vždy aktivní)</h3>
                <p className="cookie-settings-description">
                  Tyto cookies jsou nezbytné pro fungování webu a nelze je deaktivovat.
                </p>
                <ul>
                  <li>Autentizace a bezpečnost</li>
                  <li>Ukládání uživatelské relace</li>
                  <li>Základní funkčnost webu</li>
                </ul>
              </div>

              <div className="cookie-settings-section">
                <h3>Funkční cookies</h3>
                <p className="cookie-settings-description">
                  Tyto cookies zlepšují uživatelský zážitek, ale nejsou povinné.
                </p>
                <div className="cookie-setting-item">
                  <span>Ukládání nastavení</span>
                  <span className="cookie-setting-status">Vždy aktivní</span>
                </div>
              </div>

              <div className="cookie-revoke-section">
                <p className="cookie-revoke-description">
                  Můžete smazat cookies kdykoli prostřednictvím nastavení vašeho prohlížeče. 
                  Pro odvolání souhlasu se zpracováním údajů klikněte na tlačítko níže.
                </p>
                <button
                  onClick={handleRevokeConsent}
                  className="cookie-button revoke"
                >
                  Odvolat souhlas
                </button>
              </div>
            </div>

            <div className="cookie-modal-footer cookie-settings-footer">
              <button
                onClick={() => {
                  handleAccept()
                  setShowCookieSettings(false)
                }}
                className="cookie-button accept"
              >
                Přijmout všechny
              </button>
              <button
                onClick={() => {
                  handleDecline()
                  setShowCookieSettings(false)
                }}
                className="cookie-button decline"
              >
                Odmítnout všechny
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CookieConsent
